import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, Calendar, Phone, Mail, MapPin, ArrowRight, 
  ChevronDown, Heart, Brain, Users, ShieldCheck, Leaf,
  Clock, CheckCircle2, MessageCircle, FileText
} from "lucide-react";
import { Button } from "./components/ui/button";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "./components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { Checkbox } from "./components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./components/ui/radio-group";
import { Toaster, toast } from "sonner";
import axios from "axios";
import AdminPage from "./AdminPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Booking", href: "#booking" },
    { name: "FAQ", href: "#faq" },
    { name: "Contact", href: "#contact" },
  ];

  const scrollToSection = (href) => {
    setMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav shadow-sm" : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="#" 
            className="font-serif text-xl md:text-2xl text-sage-700 font-medium"
            data-testid="logo-link"
          >
            Stanzin Dolma
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="text-sage-600 hover:text-sage-800 transition-colors font-medium"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </button>
            ))}
          </div>

          {/* CTA Button - Always Visible */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => scrollToSection('#booking')}
              className="hidden sm:inline-flex btn-primary text-sm"
              data-testid="nav-book-button"
            >
              Book a Consultation
            </button>
            
            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-sage-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden mobile-menu-overlay"
            data-testid="mobile-menu"
          >
            <div className="container-custom py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="text-left text-lg text-sage-700 py-2 border-b border-sage-100"
                >
                  {link.name}
                </button>
              ))}
              <button
                onClick={() => scrollToSection('#booking')}
                className="btn-primary text-center mt-4"
                data-testid="mobile-book-button"
              >
                Book a Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      className="hero-bg min-h-screen flex items-center relative overflow-hidden"
      data-testid="hero-section"
    >
      <div className="container-custom relative z-10">
        <div className="max-w-3xl mx-auto text-center pt-20">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-sage-500 uppercase tracking-widest text-sm mb-6"
          >
            Clinical Psychology Practice
          </motion.p>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-sage-800 leading-tight mb-6"
          >
            A safe space for your healing journey
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-sage-600 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl mx-auto"
          >
            Compassionate, evidence-based therapy to help you navigate life's challenges. 
            Together, we'll work towards building self-awareness and developing healthier coping mechanisms.
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col gap-4 items-center"
          >
            <a
              href="#about"
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="hero-learn-more"
            >
              Learn About My Approach
              <ChevronDown size={18} />
            </a>
            <a
              href="#booking"
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="hero-book-button"
            >
              Book a Consultation
              <Calendar size={18} />
            </a>
          </motion.div>
        </div>
      </div>
      
      {/* Decorative element */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-sand-50 to-transparent" />
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="section-padding bg-sand-50" data-testid="about-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-2 lg:order-1"
          >
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_calm-therapy-hub/artifacts/3x1pg5nl_IMG_4564.jpg"
                alt="Stanzin Dolma - Clinical Psychologist"
                className="profile-image w-full max-w-md mx-auto"
              />
              <div className="absolute -bottom-6 -right-6 bg-sage-400 text-white p-6 rounded-2xl shadow-lg hidden md:block">
                <p className="font-serif text-2xl">6+</p>
                <p className="text-sm opacity-90">Years Experience</p>
              </div>
            </div>
          </motion.div>
          
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-1 lg:order-2"
          >
            <p className="text-sage-500 uppercase tracking-widest text-sm mb-4">About Me</p>
            <h2 className="font-serif text-3xl md:text-4xl text-sage-800 mb-6">
              Hello, I'm Stanzin Dolma
            </h2>
            <div className="space-y-4 text-sage-600 leading-relaxed">
              <p>
                I&apos;m a trained Clinical Psychologist with over 6 years of experience, an M.Phil. in Clinical Psychology from NIMHANS, Bangalore, 
                and currently pursuing my Ph.D. in Psychology. My practice is driven by a deep desire to help others 
                and a natural curiosity about human behavior.
              </p>
              <p>
                I commend your courage in seeking support—I know it takes strength to take this step, 
                especially given social stigmas. Please know that there is always hope for improvement.
              </p>
              <p>
                My approach is integrative and trauma-informed. I believe there's no "one size fits all" 
                in therapy, so I tailor my methods to what works best for you—combining CBT, DBT techniques, 
                parts work, emotional processing, and strengths-based reflections.
              </p>
            </div>
            
            {/* Qualifications */}
            <div className="mt-8 space-y-3">
              <h3 className="font-serif text-xl text-sage-700 mb-4">Qualifications</h3>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-sage-500 mt-1 flex-shrink-0" size={18} />
                <p className="text-sage-600 text-sm">M.Phil. Clinical Psychology — NIMHANS, Bangalore (2021-2022)</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-sage-500 mt-1 flex-shrink-0" size={18} />
                <p className="text-sage-600 text-sm">MA Applied Psychology (Clinical) — TISS, Mumbai (2017-2019)</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-sage-500 mt-1 flex-shrink-0" size={18} />
                <p className="text-sage-600 text-sm">B.A Hons. Psychology — Lady Shri Ram College, Delhi University</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Heart,
      title: "Individual Therapy",
      description: "One-on-one sessions tailored to your unique needs. We work together to understand your challenges and develop effective coping strategies.",
      concerns: ["Anxiety & Depression", "Stress & Burnout", "Relationship Issues"]
    },
    {
      icon: Brain,
      title: "Trauma-Informed Care",
      description: "Specialized support for processing childhood trauma, relational wounds, and grief. I create a safe space for healing at your own pace.",
      concerns: ["Childhood Trauma", "Grief & Loss", "PTSD"]
    },
    {
      icon: Users,
      title: "Parenting Support",
      description: "Guidance for parents navigating challenges with their children, including neurodiversity, academic struggles, and behavioral concerns.",
      concerns: ["Neurodiversity (ADHD, Autism)", "Executive Function", "Academic Struggles"]
    }
  ];

  return (
    <section id="services" className="section-padding bg-white grain-overlay" data-testid="services-section">
      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-sage-500 uppercase tracking-widest text-sm mb-4">Services</p>
          <h2 className="font-serif text-3xl md:text-4xl text-sage-800 mb-4">
            How I Can Help You
          </h2>
          <p className="text-sage-600 max-w-2xl mx-auto">
            I work with pre-teens, teenagers, college students, young adults, working professionals, and parents 
            facing a variety of mental health challenges.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="card-service service-card group"
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 bg-sage-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-sage-200 transition-colors">
                <service.icon className="text-sage-600" size={28} />
              </div>
              <h3 className="font-serif text-xl text-sage-800 mb-3">{service.title}</h3>
              <p className="text-sage-600 text-sm leading-relaxed mb-4">{service.description}</p>
              <div className="space-y-2">
                {service.concerns.map((concern) => (
                  <div key={concern} className="flex items-center gap-2 text-sm text-sage-500">
                    <Leaf size={14} />
                    <span>{concern}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Therapy Approach */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 bg-sage-50 rounded-3xl p-8 md:p-12"
        >
          <h3 className="font-serif text-2xl text-sage-800 mb-6 text-center">My Therapeutic Approach</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: "CBT", desc: "Cognitive Behavioral Therapy" },
              { name: "DBT", desc: "Dialectical Behavior Therapy" },
              { name: "Parts Work", desc: "Internal Family Systems" },
              { name: "Narrative", desc: "Narrative Reframing" }
            ].map((approach) => (
              <div key={approach.name} className="text-center p-4">
                <p className="font-serif text-lg text-sage-700">{approach.name}</p>
                <p className="text-sage-500 text-sm">{approach.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Session Info Section
const SessionInfoSection = () => {
  return (
    <section className="py-16 bg-sand-100" data-testid="session-info-section">
      <div className="container-custom">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Clock, label: "Session Duration", value: "50-60 minutes" },
            { icon: MapPin, label: "Mode", value: "Online Sessions" },
            { icon: MessageCircle, label: "Languages", value: "English & Hindi" },
            { icon: Calendar, label: "Availability", value: "Mon-Sat" }
          ].map((item) => (
            <div key={item.label} className="bg-white rounded-2xl p-6 text-center shadow-card">
              <item.icon className="mx-auto text-sage-500 mb-3" size={24} />
              <p className="text-sage-500 text-sm mb-1">{item.label}</p>
              <p className="font-serif text-sage-800">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Quote Section
const QuoteSection = () => {
  return (
    <section className="py-20 quote-bg" data-testid="quote-section">
      <div className="container-custom">
        <motion.blockquote
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center"
        >
          <p className="font-serif text-2xl md:text-3xl text-sage-700 italic leading-relaxed mb-6">
            "Pain is inevitable. Suffering is optional."
          </p>
          <p className="text-sage-500">— A guiding philosophy in my practice</p>
        </motion.blockquote>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      question: "What can I expect in my first session?",
      answer: "Your first session is an intake session where we'll take time to understand your challenges in detail. We'll discuss your history, identify your coping mechanisms, and explore your existing social support. This helps me understand how best to support you on your journey."
    },
    {
      question: "How long are the therapy sessions?",
      answer: "Each session is typically 50-60 minutes long. The frequency of sessions depends on your needs—we'll discuss and plan together what works best for you."
    },
    {
      question: "Is everything I share confidential?",
      answer: "Yes, confidentiality is fundamental to our therapeutic relationship. Everything shared in sessions remains private, except in cases of emergency where there's a risk to your safety or others'. Progress information may be shared with your psychiatrist (if applicable) after discussing with you."
    },
    {
      question: "Do you work with specific age groups?",
      answer: "I work with pre-teens, teenagers, college students, young adults, working professionals, and parents. My approach is tailored to each individual's developmental stage and unique circumstances."
    },
    {
      question: "What payment methods do you accept?",
      answer: "Payments are accepted via UPI, online transfer, credit or debit cards through Razorpay."
    },
    {
      question: "Can I have an introductory call before booking?",
      answer: "Absolutely! I understand you may have questions before committing to therapy. Feel free to reach out via WhatsApp or email, and we can arrange a brief introductory call to discuss any concerns."
    },
    {
      question: "What is your cancellation policy?",
      answer: "Please provide at least 24 hours' notice if you need to cancel or reschedule. Late cancellations (less than 24 hours) or no-shows are charged the full session fee (100%). Sessions booked with less than 24 hours' lead time are considered final and cannot be rescheduled or cancelled."
    }
  ];

  return (
    <section id="faq" className="section-padding bg-white" data-testid="faq-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-sage-500 uppercase tracking-widest text-sm mb-4">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl text-sage-800 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-sage-600 max-w-2xl mx-auto">
            I understand you may have questions before starting therapy. Here are some common ones.
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem 
                key={faq.question} 
                value={faq.question}
                className="bg-sand-50 rounded-2xl px-6 border-none"
                data-testid={`faq-item-${idx}`}
              >
                <AccordionTrigger className="text-left font-serif text-sage-800 hover:no-underline py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sage-600 pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

// Booking Section with Multi-Step Flow: Intake → Consent → Calendly → Payment
const BookingSection = () => {
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1 = intake, 2 = consent, 3 = calendly, 4 = payment, 5 = success
  const [intakeId, setIntakeId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const [clientType, setClientType] = useState(null); // 'new' or 'existing'
  const [existingClientEmail, setExistingClientEmail] = useState("");
  const [existingClientName, setExistingClientName] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);

  // Package state
  const [activePackages, setActivePackages] = useState([]);
  const [packageOptions, setPackageOptions] = useState(null);
  const [selectedBookingType, setSelectedBookingType] = useState(null); // 'single', 'essential_7', 'complete_12', 'use_package'
  const [isPackagePayment, setIsPackagePayment] = useState(false);
  const [packageBookingCount, setPackageBookingCount] = useState(0); // sessions booked in current sitting
  const [packageTotalToBook, setPackageTotalToBook] = useState(0); // total sessions in this package
  const [packageSessionsRemaining, setPackageSessionsRemaining] = useState(0);
  const [calendlyKey, setCalendlyKey] = useState(0); // key to force re-render Calendly widget
  
  // Intake Form State
  const [intakeData, setIntakeData] = useState({
    client_name: "",
    pronouns: "",
    age: "",
    email: "",
    occupation: "",
    location: "",
    phone: "",
    emergency_contact_phone: "",
    emergency_contact_relation: "",
    concerns: "",
    referral_source: "",
    psychiatrist_info: "",
    preferred_day_time: "",
    queries: "",
    intro_call_preference: "yes"
  });

  // Consent Form State
  const [consentData, setConsentData] = useState({
    informed_consent: false,
    confidentiality_noted: false,
    client_rights_noted: false,
    cancellation_policy_agreed: false,
    declaration_agreed: false
  });

  const handleIntakeChange = (field, value) => {
    setIntakeData({ ...intakeData, [field]: value });
  };

  // Get session fee based on client email
  const getSessionFee = () => {
    const email = (intakeData.email || existingClientEmail || "").toLowerCase();
    if (email === "krishnaypsharma04@gmail.com") return "1,500";
    return "2,500";
  };


  const handleIntakeSubmit = async () => {
    // Validate required fields - simplified list
    const requiredFields = ['client_name', 'age', 'email', 'occupation', 'phone', 
      'emergency_contact_phone', 'emergency_contact_relation', 'concerns'];
    const missingFields = requiredFields.filter(field => !intakeData[field]);
    
    if (missingFields.length > 0) {
      toast.error("Please fill all required fields.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/intake`, {
        ...intakeData,
        age: parseInt(intakeData.age),
        preferred_day_time: intakeData.preferred_day_time || "To be discussed"
      });
      setIntakeId(response.data.id);
      setCurrentStep(2);
      // Scroll to booking section to keep view consistent
      setTimeout(() => {
        document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      toast.success("Intake form submitted successfully!");
    } catch (error) {
      toast.error("Please check all fields and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConsentSubmit = async () => {
    if (!Object.values(consentData).every(v => v)) {
      toast.error("Please agree to all consent items to proceed.");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/consent`, {
        ...consentData,
        intake_id: intakeId,
        client_email: intakeData.email
      });

      if (intakeData.intro_call_preference === "yes") {
        // Free intro call — show confirmation, NO Calendly access
        setCurrentStep(7); // New step: intro call confirmation
        setTimeout(() => {
          document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        toast.success("Thank you! I'll reach out to schedule your intro call.");
      } else {
        setCurrentStep(3);
        setTimeout(() => {
          document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        toast.success("Consent form submitted! Check available time slots.");
      }
    } catch (error) {
      toast.error("Failed to submit consent form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Verify existing client email
  const handleVerifyExistingClient = async () => {
    if (!existingClientEmail) {
      toast.error("Please enter your email address.");
      return;
    }

    setIsVerifying(true);
    try {
      const response = await axios.post(`${API}/verify-client`, {
        email: existingClientEmail.toLowerCase()
      });
      
      if (response.data.exists) {
        setExistingClientName(response.data.client_name);
        setIntakeData(prev => ({
          ...prev,
          email: response.data.email,
          client_name: response.data.client_name
        }));

        // Check for active packages and fetch options in parallel
        const [pkgCheck, pkgOpts] = await Promise.all([
          axios.get(`${API}/packages/check/${encodeURIComponent(existingClientEmail.toLowerCase())}`),
          axios.get(`${API}/packages/options/${encodeURIComponent(existingClientEmail.toLowerCase())}`)
        ]);

        setActivePackages(pkgCheck.data.packages || []);
        setPackageOptions(pkgOpts.data);

        // Go to package selection step (reusing step 0 with a sub-state)
        setCurrentStep(0.5); // We'll use 0.5 as the "package selection" substep
        setTimeout(() => {
          document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        toast.success(`Welcome back, ${response.data.client_name}!`);
      } else {
        toast.error("Email not found. Please book as a new client or check your email.");
      }
    } catch (error) {
      toast.error("Failed to verify email. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle package payment
  const handlePackagePayment = async (packageType) => {
    setIsProcessingPayment(true);
    setIsPackagePayment(true);
    const pkgInfo = packageType === 'essential_7' ? { total: 7 } : { total: 12 };
    try {
      const orderResponse = await axios.post(`${API}/packages/create-order`, {
        client_name: existingClientName,
        client_email: existingClientEmail,
        client_phone: "",
        package_type: packageType
      });

      const { order_id, amount, currency, key_id } = orderResponse.data;
      setCurrentOrderId(order_id);

      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        await new Promise(resolve => script.onload = resolve);
      }

      let pollInterval = null;
      const startPolling = () => {
        pollInterval = setInterval(async () => {
          try {
            const statusRes = await axios.post(`${API}/packages/check-payment-status`, { order_id });
            if (statusRes.data.paid) {
              clearInterval(pollInterval);
              setPaymentCompleted(true);
              setIsProcessingPayment(false);
              // Refresh package data
              const pkgCheck = await axios.get(`${API}/packages/check/${encodeURIComponent(existingClientEmail.toLowerCase())}`);
              const pkgs = pkgCheck.data.packages || [];
              setActivePackages(pkgs);
              if (pkgs.length > 0) {
                setPackageTotalToBook(pkgs[0].total_sessions);
                setPackageSessionsRemaining(pkgs[0].sessions_remaining);
              }
              setPackageBookingCount(0);
              setCurrentStep(5);
              toast.success("Package payment successful! Now book your sessions.");
            }
          } catch (err) {
            if (process.env.NODE_ENV === 'development') console.warn("Package payment poll error:", err?.message);
          }
        }, 5000);
      };

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: "Stanzin Dolma",
        description: `Session Package`,
        order_id: order_id,
        handler: async function(response) {
          if (pollInterval) clearInterval(pollInterval);
          try {
            const verifyRes = await axios.post(`${API}/packages/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (verifyRes.data.verified) {
              setPaymentCompleted(true);
              const pkgCheck = await axios.get(`${API}/packages/check/${encodeURIComponent(existingClientEmail.toLowerCase())}`);
              const pkgs = pkgCheck.data.packages || [];
              setActivePackages(pkgs);
              if (pkgs.length > 0) {
                setPackageTotalToBook(pkgs[0].total_sessions);
                setPackageSessionsRemaining(pkgs[0].sessions_remaining);
              }
              setPackageBookingCount(0);
              setCurrentStep(5);
              toast.success("Package payment verified! Now book your sessions.");
            }
          } catch (err) {
            toast.error("Payment verification failed. Please contact support.");
          }
          setIsProcessingPayment(false);
        },
        modal: {
          ondismiss: function() {
            startPolling();
            setIsProcessingPayment(false);
          }
        },
        prefill: {
          name: existingClientName,
          email: existingClientEmail,
        },
        theme: { color: "#9CAF88" }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function() {
        toast.error("Payment failed. Please try again.");
        setIsProcessingPayment(false);
      });
      rzp.open();
      startPolling();
    } catch (error) {
      toast.error("Failed to initiate payment. Please try again.");
      setIsProcessingPayment(false);
    }
  };

  // Use a session from active package and go to Calendly
  const handleUsePackageSession = async () => {
    const pkg = activePackages[0];
    setIsPackagePayment(true);
    setPackageTotalToBook(pkg.total_sessions);
    setPackageSessionsRemaining(pkg.sessions_remaining);
    setPackageBookingCount(0);
    setPaymentCompleted(true);
    setSessionJustBooked(false);
    setCurrentStep(5);
    setTimeout(() => {
      document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    toast.success("Select your time slot to book a session.");
  };

  // Called when a package user confirms they've booked a Calendly slot
  const isProcessingBooking = useRef(false);
  const handlePackageSessionBooked = useCallback(async () => {
    if (isProcessingBooking.current) return; // Guard against double-fire
    isProcessingBooking.current = true;
    try {
      const res = await axios.post(`${API}/packages/use-session`, {
        email: existingClientEmail.toLowerCase()
      });
      const remaining = res.data.sessions_remaining;
      setPackageBookingCount(prev => prev + 1);
      setPackageSessionsRemaining(remaining);

      // Refresh active packages
      const pkgCheck = await axios.get(`${API}/packages/check/${encodeURIComponent(existingClientEmail.toLowerCase())}`);
      setActivePackages(pkgCheck.data.packages || []);

      if (remaining > 0) {
        toast.success(`Session booked! ${remaining} session${remaining !== 1 ? 's' : ''} remaining.`);
        // Re-render Calendly for the next booking
        setCalendlyKey(prev => prev + 1);
      } else {
        toast.success("All sessions booked! Your package is complete.");
        setCurrentStep(6);
        setTimeout(() => {
          document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } catch (error) {
      toast.error("Failed to record session. Please contact support.");
    } finally {
      // Allow next booking after a short delay (prevents rapid double-clicks too)
      setTimeout(() => { isProcessingBooking.current = false; }, 2000);
    }
  }, [existingClientEmail]);

  // Book another session from the same package (re-render Calendly)
  const handleBookAnotherSession = () => {
    setCalendlyKey(prev => prev + 1); // Force Calendly widget re-render
    setTimeout(() => {
      document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Handle Razorpay Payment
  const handlePayment = async () => {
    setIsProcessingPayment(true);
    try {
      // Create order on backend
      const orderResponse = await axios.post(`${API}/payment/create-order`, {
        client_name: intakeData.client_name || existingClientName,
        client_email: intakeData.email || existingClientEmail,
        client_phone: intakeData.phone || ""
      });

      const { order_id, amount, currency, key_id } = orderResponse.data;
      
      // Store order_id for payment status checking
      setCurrentOrderId(order_id);

      // Load Razorpay script if not already loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        await new Promise(resolve => script.onload = resolve);
      }
      
      // Start polling for payment status (for QR code payments)
      let pollInterval = null;
      const startPolling = () => {
        pollInterval = setInterval(async () => {
          try {
            const statusResponse = await axios.post(`${API}/payment/check-status`, {
              order_id: order_id
            });
            
            if (statusResponse.data.paid) {
              clearInterval(pollInterval);
              setPaymentCompleted(true);
              setCurrentStep(5);
              setTimeout(() => {
                document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
              toast.success("Payment verified! Now book your session.");
            }
          } catch (err) {
            if (process.env.NODE_ENV === 'development') console.warn("Payment poll error:", err?.message);
          }
        }, 3000); // Check every 3 seconds
      };

      // Configure Razorpay options
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        order_id: order_id,
        name: "Stanzin Dolma",
        description: "Therapy Session Fee",
        handler: async function (response) {
          // Clear polling since we got direct callback
          if (pollInterval) clearInterval(pollInterval);
          
          try {
            // Verify payment on backend
            const verifyResponse = await axios.post(`${API}/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyResponse.data.verified) {
              setPaymentCompleted(true);
              setCurrentStep(5); // Go to Calendly booking after payment
              setTimeout(() => {
                document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
              toast.success("Payment successful! Now book your session.");
            }
          } catch (error) {
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        prefill: {
          name: intakeData.client_name || existingClientName,
          email: intakeData.email || existingClientEmail,
          contact: intakeData.phone || ""
        },
        theme: {
          color: "#9CAF88"
        },
        modal: {
          ondismiss: function() {
            // Don't clear polling on dismiss - user might have paid via QR
            setIsProcessingPayment(false);
            if (currentOrderId) {
              toast.info("If you paid via QR code, please wait while we verify your payment...", {
                duration: 5000
              });
            }
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
      // Start polling after opening checkout
      startPolling();
      
    } catch (error) {
      toast.error("Failed to initiate payment. Please try again.");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Check payment status (for QR code payments)
  const checkPaymentStatus = async () => {
    if (!currentOrderId) {
      toast.error("No payment initiated. Please click 'Pay' first.");
      return;
    }
    
    setIsCheckingPayment(true);
    try {
      const response = await axios.post(`${API}/payment/check-status`, {
        order_id: currentOrderId
      });
      
      if (response.data.paid) {
        setPaymentCompleted(true);
        setCurrentStep(5);
        setTimeout(() => {
          document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        toast.success("Payment verified! Now book your session.");
      } else {
        toast.error("Payment not yet received. Please complete the payment or try again.");
      }
    } catch (error) {
      toast.error("Failed to check payment status. Please try again.");
    } finally {
      setIsCheckingPayment(false);
    }
  };

  // Load Calendly script when reaching step 3 or 5, or when calendlyKey changes (package rebooking)
  useEffect(() => {
    if (currentStep === 3 || currentStep === 5) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, [currentStep, calendlyKey]);

  // Listen for Calendly booking events via postMessage
  const [sessionJustBooked, setSessionJustBooked] = useState(false);
  useEffect(() => {
    if (currentStep !== 5) return;

    const handleCalendlyMessage = async (e) => {
      if (e.data?.event === 'calendly.event_scheduled') {
        if (isPackagePayment && packageSessionsRemaining > 0) {
          await handlePackageSessionBooked();
        } else {
          setSessionJustBooked(true);
          toast.success("Session booked successfully!");
        }
      }
    };

    window.addEventListener('message', handleCalendlyMessage);
    return () => window.removeEventListener('message', handleCalendlyMessage);
  }, [currentStep, isPackagePayment, packageSessionsRemaining, handlePackageSessionBooked]);

  // Auto-redirect countdown when on success screen (step 6)
  useEffect(() => {
    if (currentStep === 6) {
      setRedirectCountdown(5);
      const interval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            // Reset form and go to home
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setCurrentStep(0);
            setClientType(null);
            setExistingClientEmail("");
            setExistingClientName("");
            setPaymentCompleted(false);
            setCurrentOrderId(null);
            setIntakeData({
              client_name: "",
              pronouns: "",
              age: "",
              email: "",
              occupation: "",
              location: "",
              phone: "",
              emergency_contact_phone: "",
              emergency_contact_relation: "",
              concerns: "",
              referral_source: "",
              psychiatrist_info: "",
              preferred_day_time: "",
              queries: "",
              intro_call_preference: "yes"
            });
            setConsentData({
              informed_consent: false,
              confidentiality_noted: false,
              client_rights_noted: false,
              cancellation_policy_agreed: false,
              declaration_agreed: false
            });
            setIntakeId(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [currentStep]);

  const steps = [
    { num: 1, label: "Intake Form" },
    { num: 2, label: "Consent" },
    { num: 3, label: "Check Availability" },
    { num: 4, label: "Payment" },
    { num: 5, label: "Confirm Booking" }
  ];

  return (
    <section id="booking" className="section-padding bg-white" data-testid="booking-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-sage-500 uppercase tracking-widest text-sm mb-4">Book a Session</p>
          <h2 className="font-serif text-3xl md:text-4xl text-sage-800 mb-4">
            Schedule Your Consultation
          </h2>
          <p className="text-sage-600 max-w-2xl mx-auto">
            Complete the booking process in 4 simple steps to schedule your session.
          </p>
        </motion.div>

        {/* Step 0: Introduction - New/Existing Client Selection */}
        {currentStep === 0 && !clientType && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="bg-sand-50 rounded-3xl p-8 md:p-12">
              <div className="w-20 h-20 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="text-sage-600" size={36} />
              </div>
              <h3 className="font-serif text-2xl text-sage-800 mb-4">Ready to Begin?</h3>
              <p className="text-sage-600 mb-6">
                Please select whether you're a new or existing client to proceed with booking.
              </p>
              
              <div className="flex flex-wrap justify-center gap-6 mb-6 text-sage-600 text-sm">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-sage-500" />
                  <span>50-60 min sessions</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle size={16} className="text-sage-500" />
                  <span>Online via video call</span>
                </div>
              </div>

              {/* Session Fees */}
              <div className="mb-8">
                <h4 className="font-serif text-lg text-sage-700 mb-4">Session Fee</h4>
                <div className="flex justify-center">
                  <div className="bg-white rounded-2xl p-5 shadow-sm max-w-xs">
                    <p className="text-sage-500 text-sm mb-1">Per Session</p>
                    <p className="font-serif text-2xl text-sage-700">₹2,500</p>
                  </div>
                </div>
              </div>

              {/* New/Existing Client Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => {
                    setClientType('new');
                    setCurrentStep(1);
                  }}
                  className="btn-primary"
                  data-testid="new-client-btn"
                >
                  I'm a New Client
                  <ArrowRight size={18} className="ml-2" />
                </Button>
                <Button
                  onClick={() => setClientType('existing')}
                  variant="outline"
                  className="btn-secondary"
                  data-testid="existing-client-btn"
                >
                  I'm an Existing Client
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 0: Existing Client - Email Verification */}
        {currentStep === 0 && clientType === 'existing' && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto"
          >
            <div className="bg-sand-50 rounded-3xl p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="text-sage-600" size={28} />
                </div>
                <h3 className="font-serif text-xl text-sage-800 mb-2">Welcome Back!</h3>
                <p className="text-sage-600 text-sm">
                  Enter the email address you used during your first booking to continue.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-sage-600">Email Address</Label>
                  <Input
                    type="email"
                    value={existingClientEmail}
                    onChange={(e) => setExistingClientEmail(e.target.value)}
                    className="rounded-xl border-sage-200 bg-white"
                    placeholder="your.email@example.com"
                    data-testid="existing-client-email"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleVerifyExistingClient();
                      }
                    }}
                  />
                  <p className="text-sage-500 text-xs mt-2">
                    Please use the same email address you provided during your first session booking.
                  </p>
                </div>

                <Button
                  onClick={handleVerifyExistingClient}
                  className="w-full btn-primary"
                  disabled={isVerifying}
                  data-testid="verify-email-btn"
                >
                  {isVerifying ? "Verifying..." : "Check Available Slots"}
                  <ArrowRight size={18} className="ml-2" />
                </Button>

                <Button
                  onClick={() => {
                    setClientType(null);
                    setExistingClientEmail("");
                  }}
                  variant="ghost"
                  className="w-full text-sage-500 hover:text-sage-700"
                >
                  Back
                </Button>
              </div>

              <div className="mt-6 pt-6 border-t border-sage-200 text-center">
                <p className="text-sage-500 text-sm">
                  First time here?{' '}
                  <button
                    onClick={() => {
                      setClientType('new');
                      setCurrentStep(1);
                    }}
                    className="text-sage-700 font-medium hover:text-sage-800"
                  >
                    Book as a new client
                  </button>
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 0.5: Package Selection for Existing Clients */}
        {currentStep === 0.5 && clientType === 'existing' && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto"
          >
            <div className="bg-sand-50 rounded-3xl p-8" data-testid="package-selection">
              <div className="text-center mb-6">
                <h3 className="font-serif text-xl text-sage-800 mb-2">
                  Welcome back, {existingClientName}!
                </h3>
                <p className="text-sage-600 text-sm">Choose how you'd like to book your session.</p>
              </div>

              {/* Active Package Banner */}
              {activePackages.length > 0 && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-2xl" data-testid="active-package-banner">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-800 font-medium">{activePackages[0].package_label} Package</p>
                      <p className="text-green-600 text-sm">
                        {activePackages[0].sessions_remaining} of {activePackages[0].total_sessions} sessions remaining
                      </p>
                    </div>
                    <Button
                      onClick={handleUsePackageSession}
                      className="btn-primary"
                      data-testid="use-package-session-btn"
                    >
                      Book Session
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </div>
                  {/* Sessions progress bar */}
                  <div className="mt-3 w-full bg-green-100 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${(activePackages[0].sessions_remaining / activePackages[0].total_sessions) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Booking Options */}
              <div className="space-y-4">
                {/* Single Session */}
                <div
                  className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                    selectedBookingType === 'single' ? 'border-sage-500 bg-white shadow-md' : 'border-sage-200 bg-white hover:border-sage-300'
                  }`}
                  onClick={() => setSelectedBookingType('single')}
                  data-testid="option-single-session"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-sage-800">Single Session</p>
                      <p className="text-sage-500 text-sm">One-time booking</p>
                    </div>
                    <p className="font-serif text-xl text-sage-800">₹{getSessionFee()}</p>
                  </div>
                </div>

                {/* Package Options */}
                {packageOptions?.options?.map((pkg) => (
                  <div
                    key={pkg.package_type}
                    className={`p-5 rounded-2xl border-2 cursor-pointer transition-all relative overflow-hidden ${
                      selectedBookingType === pkg.package_type ? 'border-sage-500 bg-white shadow-md' : 'border-sage-200 bg-white hover:border-sage-300'
                    }`}
                    onClick={() => setSelectedBookingType(pkg.package_type)}
                    data-testid={`option-${pkg.package_type}`}
                  >
                    <div className="absolute top-0 right-0 bg-sage-600 text-white text-xs px-3 py-1 rounded-bl-xl">
                      Save {pkg.discount_pct}%
                    </div>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-sage-800">{pkg.label}</p>
                        <p className="text-sage-500 text-sm">
                          Pay for {pkg.pay_sessions} sessions, get <span className="text-sage-700 font-medium">{pkg.complimentary} complimentary</span>
                        </p>
                        <p className="text-sage-400 text-xs mt-1">
                          {pkg.total_sessions} sessions total &middot; ₹{(pkg.effective_per_session / 100).toLocaleString()} per session
                        </p>
                      </div>
                      <div className="text-right pt-2">
                        <p className="font-serif text-xl text-sage-800">₹{(pkg.total_price / 100).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Button */}
              <div className="mt-6">
                <Button
                  onClick={() => {
                    if (!selectedBookingType) {
                      toast.error("Please select a booking option.");
                      return;
                    }
                    if (selectedBookingType === 'single') {
                      setIsPackagePayment(false);
                      setCurrentStep(3); // Go to availability preview
                    } else {
                      // Go to package payment (step 4 with package flow)
                      handlePackagePayment(selectedBookingType);
                    }
                    setTimeout(() => {
                      document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  className="w-full btn-primary"
                  disabled={!selectedBookingType || isProcessingPayment}
                  data-testid="continue-booking-btn"
                >
                  {isProcessingPayment ? "Processing..." : selectedBookingType === 'single' ? "Continue with Single Session" : selectedBookingType ? "Purchase Package" : "Select an Option"}
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </div>

              <Button
                onClick={() => {
                  setClientType(null);
                  setExistingClientEmail("");
                  setSelectedBookingType(null);
                }}
                variant="ghost"
                className="w-full text-sage-500 hover:text-sage-700 mt-3"
              >
                Back
              </Button>
            </div>
          </motion.div>
        )}

        {/* Progress Indicator (visible after starting) */}
        {currentStep >= 1 && (
          <div className="flex items-center justify-center gap-2 mb-8">
            {(clientType === 'existing' ? 
              [{ num: 3, label: "Availability" }, { num: 4, label: "Payment" }, { num: 5, label: "Book" }] : 
              steps
            ).map((step, index, arr) => (
              <div key={step.num} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all
                    ${currentStep > step.num ? "bg-sage-600 text-white" : ""}
                    ${currentStep === step.num ? "bg-sage-400 text-white ring-4 ring-sage-200" : ""}
                    ${currentStep < step.num ? "bg-sage-100 text-sage-400" : ""}`}
                >
                  {currentStep > step.num ? <CheckCircle2 size={18} /> : step.num}
                </div>
                {index < arr.length - 1 && (
                  <div className={`w-8 md:w-16 h-1 mx-1 rounded ${currentStep > step.num ? "bg-sage-400" : "bg-sage-100"}`} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step 1: Intake Form */}
        {currentStep === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-2xl mx-auto"
          >
            <div className="bg-sand-50 rounded-3xl p-6 md:p-8" data-testid="intake-form">
              <h3 className="font-serif text-xl text-sage-800 mb-2">Session Booking Form</h3>
              <p className="text-sage-500 text-sm mb-6">
                Please fill this form so I can connect with you and schedule your session. All information is kept strictly confidential.
              </p>

              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sage-600">Name *</Label>
                    <Input
                      value={intakeData.client_name}
                      onChange={(e) => handleIntakeChange("client_name", e.target.value)}
                      className="rounded-xl border-sage-200 bg-white"
                      placeholder="Your full name"
                      data-testid="intake-name"
                    />
                  </div>
                  <div>
                    <Label className="text-sage-600">Age *</Label>
                    <Input
                      type="number"
                      value={intakeData.age}
                      onChange={(e) => handleIntakeChange("age", e.target.value)}
                      className="rounded-xl border-sage-200 bg-white"
                      data-testid="intake-age"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sage-600">Email *</Label>
                    <Input
                      type="email"
                      value={intakeData.email}
                      onChange={(e) => handleIntakeChange("email", e.target.value)}
                      className="rounded-xl border-sage-200 bg-white"
                      data-testid="intake-email"
                    />
                  </div>
                  <div>
                    <Label className="text-sage-600">Phone Number *</Label>
                    <Input
                      value={intakeData.phone}
                      onChange={(e) => handleIntakeChange("phone", e.target.value)}
                      className="rounded-xl border-sage-200 bg-white"
                      placeholder="+91 XXXXXXXXXX"
                      data-testid="intake-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sage-600">Occupation *</Label>
                  <Input
                    value={intakeData.occupation}
                    onChange={(e) => handleIntakeChange("occupation", e.target.value)}
                    className="rounded-xl border-sage-200 bg-white"
                    placeholder="e.g., Student, Working Professional"
                    data-testid="intake-occupation"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sage-600">Emergency Contact Number *</Label>
                    <Input
                      value={intakeData.emergency_contact_phone}
                      onChange={(e) => handleIntakeChange("emergency_contact_phone", e.target.value)}
                      className="rounded-xl border-sage-200 bg-white"
                      placeholder="Phone number of a trusted contact"
                      data-testid="intake-emergency-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-sage-600">Emergency Contact Relation *</Label>
                    <Input
                      value={intakeData.emergency_contact_relation}
                      onChange={(e) => handleIntakeChange("emergency_contact_relation", e.target.value)}
                      className="rounded-xl border-sage-200 bg-white"
                      placeholder="e.g., Parent, Spouse, Friend"
                      data-testid="intake-emergency-relation"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sage-600">Briefly Describe Your Concerns *</Label>
                  <Textarea
                    value={intakeData.concerns}
                    onChange={(e) => handleIntakeChange("concerns", e.target.value)}
                    className="rounded-xl border-sage-200 bg-white min-h-24"
                    placeholder="Share as much as you feel comfortable..."
                    data-testid="intake-concerns"
                  />
                </div>

                {/* Optional Fields */}
                <div className="pt-4 border-t border-sage-200">
                  <p className="text-sage-500 text-sm mb-4">Optional Information</p>
                  
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sage-600">Pronouns</Label>
                        <Input
                          value={intakeData.pronouns}
                          onChange={(e) => handleIntakeChange("pronouns", e.target.value)}
                          className="rounded-xl border-sage-200 bg-white"
                          placeholder="e.g., She/Her, He/Him"
                          data-testid="intake-pronouns"
                        />
                      </div>
                      <div>
                        <Label className="text-sage-600">Location</Label>
                        <Input
                          value={intakeData.location}
                          onChange={(e) => handleIntakeChange("location", e.target.value)}
                          className="rounded-xl border-sage-200 bg-white"
                          placeholder="City/State"
                          data-testid="intake-location"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-sage-600">How did you hear about me?</Label>
                      <Input
                        value={intakeData.referral_source}
                        onChange={(e) => handleIntakeChange("referral_source", e.target.value)}
                        className="rounded-xl border-sage-200 bg-white"
                        placeholder="Referral name or source"
                        data-testid="intake-referral"
                      />
                    </div>

                    <div>
                      <Label className="text-sage-600">Psychiatrist Information (if any)</Label>
                      <Input
                        value={intakeData.psychiatrist_info}
                        onChange={(e) => handleIntakeChange("psychiatrist_info", e.target.value)}
                        className="rounded-xl border-sage-200 bg-white"
                        placeholder="Name, clinic/hospital"
                        data-testid="intake-psychiatrist"
                      />
                    </div>

                    <div>
                      <Label className="text-sage-600">Any Questions for Me?</Label>
                      <Textarea
                        value={intakeData.queries}
                        onChange={(e) => handleIntakeChange("queries", e.target.value)}
                        className="rounded-xl border-sage-200 bg-white"
                        placeholder="Anything you'd like to know before we meet?"
                        data-testid="intake-queries"
                      />
                    </div>

                    <div>
                      <Label className="text-sage-600 mb-3 block">Would you like a free introductory call before booking a session?</Label>
                      <RadioGroup
                        value={intakeData.intro_call_preference}
                        onValueChange={(v) => handleIntakeChange("intro_call_preference", v)}
                        className="flex gap-6"
                        data-testid="intake-intro-call"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="intro-yes-1" />
                          <Label htmlFor="intro-yes-1" className="text-sage-700">Yes, I&apos;d like a free intro call</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id="intro-no-1" />
                          <Label htmlFor="intro-no-1" className="text-sage-700">No, I&apos;d like to book a session directly</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleIntakeSubmit}
                  className="w-full btn-primary mt-4"
                  disabled={isSubmitting}
                  data-testid="intake-submit-button"
                >
                  {isSubmitting ? "Submitting..." : "Continue to Consent Form"}
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 2: Consent Form */}
        {currentStep === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-2xl mx-auto"
          >
            <div className="bg-sand-50 rounded-3xl p-6 md:p-8" data-testid="consent-form">
              <h3 className="font-serif text-xl text-sage-800 mb-2">Consent & Confidentiality</h3>
              <p className="text-sage-500 text-sm mb-6">
                Please review and agree to the terms below before proceeding to schedule your session.
              </p>

              <div className="space-y-4">
                {/* Expandable Terms Section */}
                <Accordion type="single" collapsible className="bg-white rounded-2xl overflow-hidden">
                  <AccordionItem value="terms" className="border-none">
                    <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-sage-50">
                      <div className="flex items-center gap-3 text-left">
                        <FileText className="text-sage-500" size={20} />
                        <span className="font-medium text-sage-700">Read Terms & Conditions</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-5 pb-5">
                      <div className="space-y-4 text-sage-600 text-sm">
                        {/* Informed Consent */}
                        <div className="pb-4 border-b border-sage-100">
                          <h4 className="font-serif text-sage-800 mb-2">1. Informed Consent for Care</h4>
                          <p>
                            By agreeing below, as an 18 years+ adult client, you are providing informed consent 
                            to receive psychotherapy sessions with therapist Ms. Stanzin Dolma.
                          </p>
                        </div>

                        {/* Confidentiality */}
                        <div className="pb-4 border-b border-sage-100">
                          <h4 className="font-serif text-sage-800 mb-2">2. Confidentiality Assurance</h4>
                          <p>
                            Confidentiality in therapy means neither the content of sessions nor your personal 
                            details will be disclosed without your consent. Therapy progress may be shared with 
                            your psychiatrist (if applicable) after discussing with you. Exceptions occur only 
                            in emergencies involving risk to safety of self or others, where I may contact your 
                            emergency contact.
                          </p>
                          <p className="mt-2">
                            Undergoing regular supervision is an important practice for me as a therapist to continue 
                            to learn and grow so that I can better serve my clients. I will be sharing some details 
                            of our work with my supervisor to improve on my therapy skills. However, your identifying 
                            information will not be shared with the supervisor and anonymity will be maintained even 
                            during supervision discussions.
                          </p>
                        </div>

                        {/* Client Rights */}
                        <div className="pb-4 border-b border-sage-100">
                          <h4 className="font-serif text-sage-800 mb-2">3. Client Rights</h4>
                          <p>
                            As a client, you have the right to ask questions and refuse to answer questions if 
                            uncomfortable. You are free to stop therapy sessions anytime and seek a second opinion 
                            if you wish.
                          </p>
                        </div>

                        {/* Cancellation Policy */}
                        <div className="pb-4 border-b border-sage-100">
                          <h4 className="font-serif text-sage-800 mb-2">4. Booking, Payment & Cancellation Policy</h4>
                          <div className="space-y-3">
                            <div>
                              <p className="font-medium text-sage-700 mb-1">Booking & Payment</p>
                              <p>
                                Once you have submitted your signed consent forms, you will receive a private booking link. 
                                This link allows you to select your preferred time slot and complete your payment. 
                                Payment is required at the time of booking to secure your appointment.
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-sage-700 mb-1">24-Hour Notice for Changes</p>
                              <p>
                                I understand that life can be unpredictable. You may reschedule or cancel your session 
                                provided you give at least 24 hours' notice.
                              </p>
                              <p className="mt-1">
                                <strong>Late Cancellations/Rescheduling:</strong> If you cancel or request a reschedule 
                                with less than 24 hours' notice, the full session fee (100%) will be charged.
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-sage-700 mb-1">No-Show Policy</p>
                              <p>
                                Consistency is key to the therapeutic process. If you have not joined the session or 
                                contacted me within the first 15 minutes of our scheduled start time, it will be 
                                considered a "No-Show."
                              </p>
                              <p className="mt-1">
                                In the event of a no-show, the full session fee (100%) is forfeited.
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-sage-700 mb-1">Short-Notice Bookings</p>
                              <p>
                                Any session booked with less than 24 hours' lead time is considered a confirmed, 
                                final booking. Because the standard notice period cannot be applied to these slots, 
                                these sessions cannot be rescheduled or cancelled.
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-sage-700 mb-1">Acknowledgment</p>
                              <p>
                                There are no exceptions to the policies mentioned above. This ensures that I can 
                                maintain a sustainable practice and offer available slots to those in need. By 
                                proceeding with booking, you acknowledge that you have read, understood, and agreed 
                                to these terms.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Declaration */}
                        <div>
                          <h4 className="font-serif text-sage-800 mb-2">5. Declaration</h4>
                          <p>
                            I have read and understood the information above. I declare that the details provided 
                            are true and correct to the best of my knowledge, and I undertake to inform the 
                            therapist of any changes immediately.
                          </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                {/* Single Agree to All Toggle */}
                <div className="bg-white p-5 rounded-2xl">
                  <div className="flex items-start gap-4">
                    <Checkbox
                      id="agree-all"
                      checked={consentData.informed_consent && consentData.confidentiality_noted && 
                               consentData.client_rights_noted && consentData.cancellation_policy_agreed && 
                               consentData.declaration_agreed}
                      onCheckedChange={(checked) => {
                        setConsentData({
                          informed_consent: checked,
                          confidentiality_noted: checked,
                          client_rights_noted: checked,
                          cancellation_policy_agreed: checked,
                          declaration_agreed: checked
                        });
                      }}
                      className="mt-1"
                      data-testid="consent-agree-all"
                    />
                    <div>
                      <Label htmlFor="agree-all" className="text-sage-800 font-medium cursor-pointer">
                        I agree to all terms and conditions
                      </Label>
                      <p className="text-sage-500 text-sm mt-1">
                        By checking this box, I confirm that I have read and agree to the Informed Consent, 
                        Confidentiality Agreement, Client Rights, Cancellation Policy, and Declaration above.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep(1)}
                    className="flex-1 btn-secondary"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleConsentSubmit}
                    className="flex-1 btn-primary"
                    disabled={isSubmitting || !(consentData.informed_consent && consentData.confidentiality_noted && 
                             consentData.client_rights_noted && consentData.cancellation_policy_agreed && 
                             consentData.declaration_agreed)}
                    data-testid="consent-submit-button"
                  >
                    {isSubmitting ? "Submitting..." : "Continue to Schedule"}
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: Check Availability (Custom Display) */}
        {currentStep === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-2xl mx-auto"
          >
            <div className="bg-sand-50 rounded-3xl p-6 md:p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="text-sage-600" size={32} />
                </div>
                <h3 className="font-serif text-xl text-sage-800 mb-2">My Availability</h3>
                <p className="text-sage-500 text-sm">
                  Review my available hours below. After payment, you can select your preferred slot.
                </p>
              </div>

              {/* Availability Schedule */}
              <div className="bg-white rounded-2xl p-6 mb-6">
                <h4 className="font-medium text-sage-700 mb-4 text-center">Weekly Schedule</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-3 border-b border-sage-100">
                    <span className="text-sage-600 font-medium">Monday - Friday</span>
                    <span className="text-sage-800 bg-sage-50 px-3 py-1 rounded-full text-sm">5:00 PM - 9:00 PM</span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <span className="text-sage-600 font-medium">Saturday</span>
                    <span className="text-sage-800 bg-sage-50 px-3 py-1 rounded-full text-sm">9:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-t border-sage-100">
                    <span className="text-sage-600 font-medium">Sunday</span>
                    <span className="text-sage-400 text-sm">Closed</span>
                  </div>
                </div>
              </div>

              {/* Session Info */}
              <div className="bg-white rounded-2xl p-6 mb-6">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="p-4 bg-sage-50 rounded-xl">
                    <Clock className="text-sage-500 mx-auto mb-2" size={24} />
                    <p className="text-sage-600 text-sm">Session Duration</p>
                    <p className="text-sage-800 font-medium">50-60 minutes</p>
                  </div>
                  <div className="p-4 bg-sage-50 rounded-xl">
                    <MessageCircle className="text-sage-500 mx-auto mb-2" size={24} />
                    <p className="text-sage-600 text-sm">Session Type</p>
                    <p className="text-sage-800 font-medium">Video Call</p>
                  </div>
                </div>
              </div>

              {/* Important Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-amber-800 text-sm text-center">
                  <strong>Note:</strong> Specific time slots will be shown after payment. 
                  You'll be able to choose from all available slots on Calendly.
                </p>
              </div>
              
              {/* Payment prompt */}
              <div className="bg-gradient-to-r from-sage-100 to-sage-50 rounded-2xl p-5">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <p className="text-sage-800 font-medium">Ready to book?</p>
                    <p className="text-sage-600 text-sm">Pay to unlock the full calendar and book your slot.</p>
                  </div>
                  <Button
                    onClick={() => {
                      setCurrentStep(4);
                      setTimeout(() => {
                        document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                    className="btn-primary whitespace-nowrap"
                    data-testid="proceed-to-payment-btn"
                  >
                    Proceed with Booking
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
                {clientType !== 'existing' && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCurrentStep(2);
                      setTimeout(() => {
                        document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                    className="btn-secondary"
                  >
                    Back to Consent
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 4: Payment */}
        {currentStep === 4 && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-xl mx-auto"
          >
            <div className="bg-sand-50 rounded-3xl p-8 text-center">
              <div className="w-20 h-20 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <ShieldCheck className="text-sage-600" size={36} />
              </div>
              
              <h3 className="font-serif text-2xl text-sage-800 mb-4">Complete Your Payment</h3>
              
              {/* Important Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6">
                <p className="text-amber-800 text-sm font-medium">
                  Important: Your session will only be confirmed once payment is completed.
                </p>
                <p className="text-amber-700 text-sm mt-2">
                  After payment, you can select and confirm your preferred time slot.
                </p>
              </div>

              <p className="text-sage-600 mb-6">
                Click the button below to securely pay via Razorpay. After successful payment, you can book your session.
              </p>

              {/* Pricing Info */}
              <div className="bg-white rounded-2xl p-6 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-sage-600">Session Fee</span>
                  <span className="font-serif text-xl text-sage-800">₹{getSessionFee()}</span>
                </div>
              </div>

              <Button
                onClick={handlePayment}
                disabled={isProcessingPayment}
                className="w-full btn-primary mb-3"
                data-testid="pay-now-btn"
              >
                {isProcessingPayment ? "Processing..." : `Pay ₹${getSessionFee()} Now`}
                <ArrowRight size={18} className="ml-2" />
              </Button>

              {/* Check Payment Status - for QR code payments */}
              {currentOrderId && (
                <Button
                  onClick={checkPaymentStatus}
                  disabled={isCheckingPayment}
                  variant="outline"
                  className="w-full btn-secondary mb-3"
                  data-testid="check-payment-btn"
                >
                  {isCheckingPayment ? "Checking..." : "I've Paid - Verify Payment"}
                </Button>
              )}

              <p className="text-sage-500 text-xs mb-4">
                Secure payment powered by Razorpay. Supports UPI, cards, and net banking.
              </p>
              
              <p className="text-sage-400 text-xs mb-6">
                Paid via QR code? Click "I've Paid - Verify Payment" to confirm.
              </p>

              <Button
                variant="ghost"
                onClick={() => {
                  setCurrentStep(3);
                  setTimeout(() => {
                    document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
                className="text-sage-500 hover:text-sage-700"
                disabled={isProcessingPayment}
              >
                Back to Check Availability
              </Button>

              <div className="mt-8 pt-6 border-t border-sage-200">
                <p className="text-sage-500 text-sm">
                  Questions? Contact me at{' '}
                  <a href="tel:+919217736078" className="text-sage-600 hover:text-sage-800">+91 9217736078</a>
                  {' '}or{' '}
                  <a href="mailto:stanzindolma.work@gmail.com" className="text-sage-600 hover:text-sage-800">stanzindolma.work@gmail.com</a>
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 5: Calendly Booking (after payment) */}
        {currentStep === 5 && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-sand-50 rounded-3xl p-6 md:p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="text-green-600" size={32} />
                </div>
                <h3 className="font-serif text-xl text-sage-800 mb-2">
                  {isPackagePayment && packageSessionsRemaining > 0
                    ? "Book Your Sessions"
                    : "Payment Successful!"}
                </h3>
                <p className="text-sage-500 text-sm">
                  {isPackagePayment && packageSessionsRemaining > 0
                    ? "Select your preferred time slots below. You can book one session at a time."
                    : "Now select your preferred time slot to complete your booking."}
                </p>
              </div>

              {/* Package Session Counter */}
              {isPackagePayment && packageSessionsRemaining > 0 && (
                <div className="mb-6 p-4 bg-white rounded-2xl border border-sage-200" data-testid="package-session-counter">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sage-600 font-medium">Session Progress</span>
                    <span className="text-sage-800 font-serif">
                      {packageBookingCount} of {packageTotalToBook} booked
                    </span>
                  </div>
                  <div className="w-full bg-sage-100 rounded-full h-3">
                    <div
                      className="bg-sage-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${(packageBookingCount / packageTotalToBook) * 100}%` }}
                    />
                  </div>
                  <p className="text-sage-400 text-xs mt-2 text-center">
                    {packageSessionsRemaining} session{packageSessionsRemaining !== 1 ? 's' : ''} remaining to schedule
                  </p>
                </div>
              )}

              {/* Calendly Inline Widget */}
              <div
                key={calendlyKey}
                className="calendly-inline-widget rounded-2xl overflow-hidden bg-white" 
                data-url={`https://calendly.com/stanzindolma-work?hide_gdpr_banner=1&background_color=ffffff&text_color=2d3748&primary_color=9caf88&name=${encodeURIComponent(intakeData.client_name || existingClientName)}&email=${encodeURIComponent(intakeData.email || existingClientEmail)}`}
                style={{ minWidth: '320px', height: '600px' }}
                data-testid="calendly-embed"
              />

              <div className="mt-6 flex flex-col items-center gap-3">
                {/* For package users */}
                {isPackagePayment && packageSessionsRemaining > 0 ? (
                  <>
                    {packageBookingCount > 0 && (
                      <div className="w-full max-w-md p-3 bg-green-50 border border-green-200 rounded-xl text-center mb-2">
                        <p className="text-green-700 text-sm font-medium">
                          Session {packageBookingCount} booked automatically
                        </p>
                        <p className="text-green-600 text-xs">Select another time slot above to book your next session</p>
                      </div>
                    )}

                    <p className="text-sage-400 text-xs text-center max-w-md">
                      Your session counter updates automatically when you complete a booking on Calendly above.
                      {packageBookingCount === 0 && " Select a time slot to get started."}
                    </p>

                    {/* Fallback manual button - in case auto-detect doesn't fire */}
                    <Button
                      onClick={async () => {
                        await handlePackageSessionBooked();
                      }}
                      variant="outline"
                      className="w-full max-w-md text-sage-600"
                      data-testid="confirm-session-booked-btn"
                    >
                      Booked but counter didn't update? Click here
                    </Button>

                    {packageBookingCount > 0 && (
                      <Button
                        onClick={() => {
                          setCurrentStep(6);
                          setTimeout(() => {
                            document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 100);
                        }}
                        className="w-full max-w-md btn-primary"
                        data-testid="done-for-now-btn"
                      >
                        {packageSessionsRemaining > 0
                          ? `Done for Now — Book Remaining ${packageSessionsRemaining} Later`
                          : "All Sessions Booked — Continue"}
                      </Button>
                    )}
                  </>
                ) : (
                  /* For single session users */
                  <Button
                    onClick={() => {
                      setCurrentStep(6);
                      setTimeout(() => {
                        document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                    className={sessionJustBooked ? "btn-primary animate-pulse" : "btn-primary"}
                    data-testid="complete-booking-btn"
                  >
                    {sessionJustBooked ? "Session Booked — Continue" : "I've Scheduled My Session"}
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 6: Success Screen */}
        {currentStep === 6 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-xl mx-auto"
          >
            <div className="bg-sand-50 rounded-3xl p-8 text-center">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="text-green-600" size={48} />
              </div>
              
              <h3 className="font-serif text-2xl text-sage-800 mb-4">Thank You!</h3>
              
              <p className="text-sage-600 mb-6">
                {isPackagePayment && packageSessionsRemaining > 0
                  ? `You've booked ${packageBookingCount} session${packageBookingCount !== 1 ? 's' : ''}. You can book your remaining sessions anytime by returning as an existing client.`
                  : "Your booking request has been received. You will receive a confirmation email within 2 hours once your payment is verified and session is confirmed."}
              </p>

              {/* Remaining sessions for package users */}
              {isPackagePayment && packageSessionsRemaining > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6 text-left" data-testid="remaining-sessions-notice">
                  <p className="text-amber-800 font-medium mb-2">Sessions Remaining</p>
                  <p className="text-amber-700 text-sm mb-3">
                    You have <span className="font-bold">{packageSessionsRemaining}</span> session{packageSessionsRemaining !== 1 ? 's' : ''} left in your package. Come back anytime using your email to book them.
                  </p>
                  <div className="w-full bg-amber-100 rounded-full h-2">
                    <div
                      className="bg-amber-500 h-2 rounded-full transition-all"
                      style={{ width: `${(packageSessionsRemaining / packageTotalToBook) * 100}%` }}
                    />
                  </div>
                  <p className="text-amber-600 text-xs mt-2">{packageBookingCount} booked &middot; {packageSessionsRemaining} remaining &middot; {packageTotalToBook} total</p>
                </div>
              )}

              <div className="bg-white rounded-2xl p-6 mb-6 text-left">
                <h4 className="font-medium text-sage-700 mb-3">What's Next?</h4>
                <ul className="space-y-2 text-sage-600 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Payment verification (within 2 hours)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Confirmation email with session details</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Video call link sent before session</span>
                  </li>
                </ul>
              </div>

              {/* Auto-redirect notice */}
              <div className="bg-sage-100 rounded-xl p-4 mb-6">
                <p className="text-sage-700 text-sm">
                  Redirecting to homepage in <span className="font-semibold">{redirectCountdown}</span> seconds...
                </p>
              </div>

              <Button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setCurrentStep(0);
                  setClientType(null);
                  setExistingClientEmail("");
                  setExistingClientName("");
                  setPaymentCompleted(false);
                  // Reset form data
                  setIntakeData({
                    client_name: "",
                    pronouns: "",
                    age: "",
                    email: "",
                    occupation: "",
                    location: "",
                    phone: "",
                    emergency_contact_phone: "",
                    emergency_contact_relation: "",
                    concerns: "",
                    referral_source: "",
                    psychiatrist_info: "",
                    preferred_day_time: "",
                    queries: "",
                    intro_call_preference: "yes"
                  });
                  setConsentData({
                    informed_consent: false,
                    confidentiality_noted: false,
                    client_rights_noted: false,
                    cancellation_policy_agreed: false,
                    declaration_agreed: false
                  });
                  setIntakeId(null);
                }}
                className="btn-primary"
                data-testid="back-to-home-btn"
              >
                Go to Home Now
              </Button>

              <div className="mt-8 pt-6 border-t border-sage-200">
                <p className="text-sage-500 text-sm">
                  Questions? Contact me at{' '}
                  <a href="tel:+919217736078" className="text-sage-600 hover:text-sage-800">+91 9217736078</a>
                  {' '}or{' '}
                  <a href="mailto:stanzindolma.work@gmail.com" className="text-sage-600 hover:text-sage-800">stanzindolma.work@gmail.com</a>
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 7: Intro Call Confirmation (no Calendly, no payment) */}
        {currentStep === 7 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto text-center"
          >
            <div className="bg-sand-50 rounded-3xl p-8 md:p-10" data-testid="intro-call-confirmation">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone className="text-green-600" size={36} />
              </div>

              <h3 className="font-serif text-2xl text-sage-800 mb-4">
                Thank You, {intakeData.client_name || ""}!
              </h3>

              <p className="text-sage-600 mb-6 leading-relaxed">
                I&apos;ve received your details and will personally reach out to you within 
                <span className="font-medium text-sage-800"> 24-48 hours</span> to schedule your 
                complimentary introductory call.
              </p>

              <div className="bg-white rounded-2xl p-6 mb-6 text-left">
                <h4 className="font-medium text-sage-700 mb-3">What to Expect</h4>
                <ul className="space-y-3 text-sage-600 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>I&apos;ll contact you via phone or email to find a convenient time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>The intro call is a brief, no-obligation conversation (15-20 minutes)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>We&apos;ll discuss your needs and how I can help</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>If you&apos;d like to proceed, you can book a full session after the call</span>
                  </li>
                </ul>
              </div>

              <div className="bg-sage-50 rounded-xl p-4 mb-6 text-left">
                <p className="text-sage-700 text-sm">
                  <span className="font-medium">Your contact details:</span><br />
                  {intakeData.email} &middot; {intakeData.phone}
                </p>
              </div>

              <Button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setCurrentStep(0);
                  setClientType(null);
                }}
                className="btn-primary"
                data-testid="intro-call-home-btn"
              >
                Back to Home
              </Button>

              <div className="mt-8 pt-6 border-t border-sage-200">
                <p className="text-sage-500 text-sm">
                  Questions? Contact me at{' '}
                  <a href="tel:+919217736078" className="text-sage-600 hover:text-sage-800">+91 9217736078</a>
                  {' '}or{' '}
                  <a href="mailto:stanzindolma.work@gmail.com" className="text-sage-600 hover:text-sage-800">stanzindolma.work@gmail.com</a>
                </p>
              </div>
            </div>
          </motion.div>
        )}

      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="section-padding bg-sand-50" data-testid="contact-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sage-500 uppercase tracking-widest text-sm mb-4">Get in Touch</p>
            <h2 className="font-serif text-3xl md:text-4xl text-sage-800 mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-sage-600 mb-8 leading-relaxed">
              Taking the first step towards therapy takes courage. I&apos;m here to support you. 
              Reach out to schedule a session or ask any questions you may have.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center">
                  <Phone className="text-sage-600" size={20} />
                </div>
                <div>
                  <p className="text-sage-500 text-sm">Phone / WhatsApp</p>
                  <a href="tel:+919217736078" className="text-sage-800 font-medium hover:text-sage-600">
                    +91 9217736078
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center">
                  <Mail className="text-sage-600" size={20} />
                </div>
                <div>
                  <p className="text-sage-500 text-sm">Email</p>
                  <a href="mailto:stanzindolma.work@gmail.com" className="text-sage-800 font-medium hover:text-sage-600">
                    stanzindolma.work@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center">
                  <Clock className="text-sage-600" size={20} />
                </div>
                <div>
                  <p className="text-sage-500 text-sm">Available Hours</p>
                  <p className="text-sage-800 font-medium">Mon-Fri: 5PM-9PM | Sat: 9AM-4PM</p>
                </div>
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href="#booking"
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="contact-book-button"
              >
                <Calendar size={18} />
                Book a Session
              </a>
              <a
                href="https://razorpay.me/@stanzindolma"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center justify-center gap-2"
                data-testid="contact-payment-button"
              >
                Make a Payment
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-sage-800 text-white py-12" data-testid="footer">
      <div className="container-custom">
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <h3 className="font-serif text-xl mb-4">Stanzin Dolma</h3>
            <p className="text-sage-200 text-sm leading-relaxed">
              Clinical Psychologist & Psychotherapist<br />
              M.Phil. Clinical Psychology, NIMHANS
            </p>
          </div>
          
          <div>
            <h4 className="font-medium mb-4">Quick Links</h4>
            <div className="space-y-2">
              {["About", "Services", "FAQ", "Contact"].map((link) => (
                <a 
                  key={link}
                  href={`#${link.toLowerCase()}`}
                  className="block text-sage-200 hover:text-white text-sm transition-colors"
                >
                  {link}
                </a>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="font-medium mb-4">Contact</h4>
            <div className="space-y-2 text-sage-200 text-sm">
              <p>+91 9217736078</p>
              <p>stanzindolma.work@gmail.com</p>
              <p>Delhi, India</p>
            </div>
          </div>
        </div>
        
        <div className="border-t border-sage-700 mt-8 pt-8 text-center text-sage-300 text-sm">
          <p>© {new Date().getFullYear()} Stanzin Dolma. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Booking Flow Modal
const BookingFlowModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [intakeId, setIntakeId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Intake Form State
  const [intakeData, setIntakeData] = useState({
    client_name: "",
    pronouns: "",
    age: "",
    email: "",
    occupation: "",
    location: "",
    phone: "",
    emergency_contact_phone: "",
    emergency_contact_relation: "",
    concerns: "",
    referral_source: "",
    psychiatrist_info: "",
    preferred_day_time: "",
    queries: "",
    intro_call_preference: "yes"
  });

  // Consent Form State
  const [consentData, setConsentData] = useState({
    informed_consent: false,
    confidentiality_noted: false,
    client_rights_noted: false,
    cancellation_policy_agreed: false,
    declaration_agreed: false
  });

  const handleIntakeChange = (field, value) => {
    setIntakeData({ ...intakeData, [field]: value });
  };

  const handleIntakeSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/intake`, {
        ...intakeData,
        age: parseInt(intakeData.age)
      });
      setIntakeId(response.data.id);
      setStep(2);
      toast.success("Intake form submitted successfully!");
    } catch (error) {
      toast.error("Please fill all required fields correctly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConsentSubmit = async () => {
    if (!Object.values(consentData).every(v => v)) {
      toast.error("Please agree to all consent items to proceed.");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/consent`, {
        ...consentData,
        intake_id: intakeId,
        client_email: intakeData.email
      });
      setStep(3);
      toast.success("Consent form submitted! You can now book your session.");
    } catch (error) {
      toast.error("Failed to submit consent form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setStep(1);
    setIntakeId(null);
    setIntakeData({
      client_name: "",
      pronouns: "",
      age: "",
      email: "",
      occupation: "",
      location: "",
      phone: "",
      emergency_contact_phone: "",
      emergency_contact_relation: "",
      concerns: "",
      referral_source: "",
      psychiatrist_info: "",
      preferred_day_time: "",
      queries: "",
      intro_call_preference: "yes"
    });
    setConsentData({
      informed_consent: false,
      confidentiality_noted: false,
      client_rights_noted: false,
      cancellation_policy_agreed: false,
      declaration_agreed: false
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-sand-50">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-sage-800">
            {step === 1 && "Session Booking & Intake Form"}
            {step === 2 && "Consent & Confidentiality"}
            {step === 3 && "Book Your Session"}
          </DialogTitle>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium progress-step
                ${step === s ? "active" : ""} 
                ${step > s ? "completed" : "bg-sage-100 text-sage-400"}`}
            >
              {step > s ? <CheckCircle2 size={18} /> : s}
            </div>
          ))}
        </div>

        {/* Step 1: Intake Form */}
        {step === 1 && (
          <div className="space-y-4" data-testid="intake-form">
            <p className="text-sage-600 text-sm mb-4">
              Thank you for reaching out. Please fill this form so I can connect with you and schedule your session.
              All information will be kept strictly confidential.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-600">Name *</Label>
                <Input
                  value={intakeData.client_name}
                  onChange={(e) => handleIntakeChange("client_name", e.target.value)}
                  className="rounded-xl border-sage-200"
                  placeholder="Your full name"
                  data-testid="intake-name"
                />
              </div>
              <div>
                <Label className="text-sage-600">Pronouns *</Label>
                <Input
                  value={intakeData.pronouns}
                  onChange={(e) => handleIntakeChange("pronouns", e.target.value)}
                  className="rounded-xl border-sage-200"
                  placeholder="e.g., She/Her, He/Him"
                  data-testid="intake-pronouns"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-600">Age *</Label>
                <Input
                  type="number"
                  value={intakeData.age}
                  onChange={(e) => handleIntakeChange("age", e.target.value)}
                  className="rounded-xl border-sage-200"
                  data-testid="intake-age"
                />
              </div>
              <div>
                <Label className="text-sage-600">Email *</Label>
                <Input
                  type="email"
                  value={intakeData.email}
                  onChange={(e) => handleIntakeChange("email", e.target.value)}
                  className="rounded-xl border-sage-200"
                  data-testid="intake-email"
                />
              </div>
            </div>

            <div>
              <Label className="text-sage-600">Occupation *</Label>
              <Input
                value={intakeData.occupation}
                onChange={(e) => handleIntakeChange("occupation", e.target.value)}
                className="rounded-xl border-sage-200"
                placeholder="e.g., Student, Working Professional"
                data-testid="intake-occupation"
              />
            </div>

            <div>
              <Label className="text-sage-600">Current Location/Address *</Label>
              <Input
                value={intakeData.location}
                onChange={(e) => handleIntakeChange("location", e.target.value)}
                className="rounded-xl border-sage-200"
                data-testid="intake-location"
              />
            </div>

            <div>
              <Label className="text-sage-600">Personal Phone Number *</Label>
              <Input
                value={intakeData.phone}
                onChange={(e) => handleIntakeChange("phone", e.target.value)}
                className="rounded-xl border-sage-200"
                placeholder="+91 XXXXXXXXXX"
                data-testid="intake-phone"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-600">Emergency Contact Number *</Label>
                <Input
                  value={intakeData.emergency_contact_phone}
                  onChange={(e) => handleIntakeChange("emergency_contact_phone", e.target.value)}
                  className="rounded-xl border-sage-200"
                  data-testid="intake-emergency-phone"
                />
              </div>
              <div>
                <Label className="text-sage-600">Relation with Emergency Contact *</Label>
                <Input
                  value={intakeData.emergency_contact_relation}
                  onChange={(e) => handleIntakeChange("emergency_contact_relation", e.target.value)}
                  className="rounded-xl border-sage-200"
                  placeholder="e.g., Parent, Spouse"
                  data-testid="intake-emergency-relation"
                />
              </div>
            </div>

            <div>
              <Label className="text-sage-600">Briefly Describe Your Concerns *</Label>
              <Textarea
                value={intakeData.concerns}
                onChange={(e) => handleIntakeChange("concerns", e.target.value)}
                className="rounded-xl border-sage-200 min-h-24"
                placeholder="Share as much as you feel comfortable..."
                data-testid="intake-concerns"
              />
            </div>

            <div>
              <Label className="text-sage-600">How did you hear about me? *</Label>
              <Input
                value={intakeData.referral_source}
                onChange={(e) => handleIntakeChange("referral_source", e.target.value)}
                className="rounded-xl border-sage-200"
                placeholder="Referral name or source"
                data-testid="intake-referral"
              />
            </div>

            <div>
              <Label className="text-sage-600">Psychiatrist Information (if any)</Label>
              <Input
                value={intakeData.psychiatrist_info}
                onChange={(e) => handleIntakeChange("psychiatrist_info", e.target.value)}
                className="rounded-xl border-sage-200"
                placeholder="Name, clinic/hospital (leave blank if N/A)"
                data-testid="intake-psychiatrist"
              />
            </div>

            <div>
              <Label className="text-sage-600">Preferred Day & Time for Sessions *</Label>
              <Input
                value={intakeData.preferred_day_time}
                onChange={(e) => handleIntakeChange("preferred_day_time", e.target.value)}
                className="rounded-xl border-sage-200"
                placeholder="e.g., Weekday evenings, Saturday mornings"
                data-testid="intake-preferred-time"
              />
            </div>

            <div>
              <Label className="text-sage-600">Any Questions for Me?</Label>
              <Textarea
                value={intakeData.queries}
                onChange={(e) => handleIntakeChange("queries", e.target.value)}
                className="rounded-xl border-sage-200"
                placeholder="Anything you'd like to know before we meet?"
                data-testid="intake-queries"
              />
            </div>

            <div>
              <Label className="text-sage-600 mb-3 block">Would you like an introductory call before booking?</Label>
              <RadioGroup
                value={intakeData.intro_call_preference}
                onValueChange={(v) => handleIntakeChange("intro_call_preference", v)}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="intro-yes" />
                  <Label htmlFor="intro-yes">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="intro-no" />
                  <Label htmlFor="intro-no">No</Label>
                </div>
              </RadioGroup>
            </div>

            <Button
              onClick={handleIntakeSubmit}
              className="w-full btn-primary mt-4"
              disabled={isSubmitting}
              data-testid="intake-submit-button"
            >
              {isSubmitting ? "Submitting..." : "Continue to Consent Form"}
            </Button>
          </div>
        )}

        {/* Step 2: Consent Form */}
        {step === 2 && (
          <div className="space-y-6" data-testid="consent-form">
            <p className="text-sage-600 text-sm">
              Please read and acknowledge the following carefully. This contains important information about 
              consent, confidentiality, and our working agreement.
            </p>

            {/* Informed Consent */}
            <div className="bg-white p-6 rounded-2xl space-y-3">
              <h4 className="font-serif text-sage-800">Informed Consent for Care *</h4>
              <p className="text-sage-600 text-sm">
                By checking "I agree" as an 18 years+ adult client, you are providing informed consent 
                to receive psychotherapy sessions with therapist Ms. Stanzin.
              </p>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="informed-consent"
                  checked={consentData.informed_consent}
                  onCheckedChange={(c) => setConsentData({ ...consentData, informed_consent: c })}
                  data-testid="consent-informed"
                />
                <Label htmlFor="informed-consent" className="text-sage-700">I agree</Label>
              </div>
            </div>

            {/* Confidentiality */}
            <div className="bg-white p-6 rounded-2xl space-y-3">
              <h4 className="font-serif text-sage-800">Confidentiality Assurance *</h4>
              <p className="text-sage-600 text-sm">
                Confidentiality in therapy means neither the content of sessions nor your personal details 
                will be disclosed without your consent. Therapy progress may be shared with your psychiatrist 
                (if applicable) after discussing with you. Exceptions occur only in emergencies involving 
                risk to safety of self or others, where I may contact your emergency contact.
              </p>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="confidentiality"
                  checked={consentData.confidentiality_noted}
                  onCheckedChange={(c) => setConsentData({ ...consentData, confidentiality_noted: c })}
                  data-testid="consent-confidentiality"
                />
                <Label htmlFor="confidentiality" className="text-sage-700">Noted</Label>
              </div>
            </div>

            {/* Client Rights */}
            <div className="bg-white p-6 rounded-2xl space-y-3">
              <h4 className="font-serif text-sage-800">Client Rights *</h4>
              <p className="text-sage-600 text-sm">
                As a client, you have the right to ask questions and refuse to answer questions if uncomfortable. 
                You are free to stop therapy sessions anytime and seek a second opinion if you wish.
              </p>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="client-rights"
                  checked={consentData.client_rights_noted}
                  onCheckedChange={(c) => setConsentData({ ...consentData, client_rights_noted: c })}
                  data-testid="consent-rights"
                />
                <Label htmlFor="client-rights" className="text-sage-700">Noted</Label>
              </div>
            </div>

            {/* Cancellation Policy */}
            <div className="bg-white p-6 rounded-2xl space-y-3">
              <h4 className="font-serif text-sage-800">Cancellation Policy *</h4>
              <p className="text-sage-600 text-sm">
                If you need to cancel or reschedule, please inform me at least 24 hours in advance. 
                Cancellations with less than 24 hours notice will be charged 50% of the session fee. 
                Occasional emergencies will be handled with flexibility. Repeated last-minute cancellations 
                or missed sessions (3 consecutive) will require full payment.
              </p>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="cancellation"
                  checked={consentData.cancellation_policy_agreed}
                  onCheckedChange={(c) => setConsentData({ ...consentData, cancellation_policy_agreed: c })}
                  data-testid="consent-cancellation"
                />
                <Label htmlFor="cancellation" className="text-sage-700">I agree</Label>
              </div>
            </div>

            {/* Declaration */}
            <div className="bg-white p-6 rounded-2xl space-y-3">
              <h4 className="font-serif text-sage-800">Declaration *</h4>
              <p className="text-sage-600 text-sm">
                I have read and understood the information above. I declare that the details provided are 
                true and correct to the best of my knowledge, and I undertake to inform the therapist 
                of any changes immediately.
              </p>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="declaration"
                  checked={consentData.declaration_agreed}
                  onCheckedChange={(c) => setConsentData({ ...consentData, declaration_agreed: c })}
                  data-testid="consent-declaration"
                />
                <Label htmlFor="declaration" className="text-sage-700">I agree</Label>
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 btn-secondary"
              >
                Back
              </Button>
              <Button
                onClick={handleConsentSubmit}
                className="flex-1 btn-primary"
                disabled={isSubmitting}
                data-testid="consent-submit-button"
              >
                {isSubmitting ? "Submitting..." : "Submit & Continue"}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Booking Confirmation */}
        {step === 3 && (
          <div className="text-center py-8" data-testid="booking-complete">
            <div className="w-20 h-20 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="text-sage-600" size={40} />
            </div>
            <h3 className="font-serif text-2xl text-sage-800 mb-4">
              You're All Set!
            </h3>
            <p className="text-sage-600 mb-8">
              Your intake and consent forms have been submitted successfully. 
              Click below to schedule your session through Google Calendar.
            </p>

            <div className="space-y-4">
              <a
                href="https://calendar.google.com/calendar/u/3/r/appointment"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full btn-primary text-center"
                data-testid="final-book-button"
              >
                <Calendar className="inline mr-2" size={18} />
                Book Your Session
              </a>
              
              <a
                href="https://razorpay.me/@stanzindolma"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full btn-secondary text-center"
                data-testid="final-payment-button"
              >
                Make Payment (Razorpay)
              </a>

              <Button
                variant="ghost"
                onClick={resetAndClose}
                className="text-sage-500"
              >
                Close
              </Button>
            </div>

            <p className="text-sage-400 text-sm mt-6">
              Questions? Reach out at +91 9217736078 or stanzindolma.work@gmail.com
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Home Page Component
function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Toaster position="top-center" richColors />
      <Navigation scrolled={scrolled} />
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <SessionInfoSection />
        <QuoteSection />
        <FAQSection />
        <BookingSection />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
}

// Main App Component with Routing
function App() {
  return (
    <BrowserRouter>
      <div className="App">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
